import { AnimatePresence, motion } from "framer-motion";
import { Hammer, MapPin, Mountain, ScrollText, X } from "lucide-react";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import { EASE } from "@/components/Reveal";

export default function ProjectLightbox({ project, onClose }) {
  return (
    <AnimatePresence>
      {project && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-obsidian/90 p-4 backdrop-blur-sm sm:p-8"
          onClick={onClose}
          data-testid="project-lightbox"
        >
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.5, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative grid max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-charcoal-card ring-1 ring-gold/25 md:grid-cols-2"
          >
            <button
              onClick={onClose}
              data-testid="lightbox-close"
              className="absolute right-4 top-4 z-10 rounded-full bg-obsidian/70 p-2 text-stone-300 backdrop-blur transition-colors hover:text-gold"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="min-h-[280px]">
              <img src={project.image} alt={project.name} className="h-full w-full object-cover" />
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-10">
              <span className="eyebrow-light">{project.category}</span>
              <h3 className="mt-3 font-serif text-3xl font-medium text-stone-100 sm:text-4xl">{project.name}</h3>
              <p className="mt-4 text-sm leading-relaxed text-stone-400">{project.description}</p>
              <dl className="mt-7 space-y-4 text-sm">
                {[
                  { icon: MapPin, label: "Location", value: project.location },
                  { icon: ScrollText, label: "Project Type", value: project.type },
                  { icon: Mountain, label: "Materials / Craft", value: project.materials },
                  { icon: Hammer, label: "Services", value: project.services },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex gap-3 border-b border-charcoal-border pb-3">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.25em] text-stone-500">{label}</dt>
                      <dd className="mt-1 text-stone-300">{value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
              <WhatsAppCTA
                label="Inquire About a Similar Project"
                message={`Hello ShilpStones Creations, I loved your project "${project.name}" and would like to discuss something similar.`}
                context={`Project inquiry: ${project.name}`}
                testId="lightbox-whatsapp-cta"
                className="mt-8 w-fit"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
